// 轻量提示（不依赖 alert/第三方组件），避免触发任何新窗口行为
// 用法：
// - showUiHint('已缓存(可离线播放)')                         -> 底部 toast
// - showUiHint('已缓存(可离线播放)', { anchorEl: el })        -> 显示在元素旁边
// - showHintPlus('权限提示文案')                             -> 屏幕居中、大号字体、渐变蓝底、6 秒（用于权限类提示）
let activeEl = null;
let activeTimer = null;
let activeCleanup = null;

let hintPlusEl = null;
let hintPlusTimer = null;

export function showUiHint(text, opts = {}) {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const msg = String(text ?? '').trim();
    if (!msg) return;

    const duration = Math.max(600, Math.min(5000, Number(opts.duration ?? 1600)));
    const anchorEl = opts && opts.anchorEl ? opts.anchorEl : null;
    const placement = String(opts.placement || 'right').toLowerCase(); // right | left
    const noWrap = !!(opts && opts.noWrap);

    // 清理上一个
    try {
      if (activeTimer) window.clearTimeout(activeTimer);
      activeTimer = null;
      if (activeEl && activeEl.parentNode) activeEl.parentNode.removeChild(activeEl);
    } catch (_) {}
    activeEl = null;
    try { if (typeof activeCleanup === 'function') activeCleanup(); } catch (_) {}
    activeCleanup = null;

    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = msg;
    el.style.cssText = [
      'position:fixed',
      'z-index:2147483647',
      'max-width:min(92vw, 560px)',
      'padding:8px 10px',
      'border-radius:12px',
      'background:rgba(17,24,39,0.88)',
      'color:#fff',
      'font-size:12px',
      'line-height:1.35',
      'letter-spacing:0.2px',
      'box-shadow:0 18px 40px rgba(0,0,0,0.45)',
      'border:1px solid rgba(255,255,255,0.14)',
      'backdrop-filter:blur(10px)',
      '-webkit-backdrop-filter:blur(10px)',
      'pointer-events:none',
      'opacity:0',
      'transition:opacity 140ms ease, transform 140ms ease',
      (!noWrap && msg.includes('\n')) ? 'white-space:pre-line' : '',
      noWrap ? 'white-space:nowrap' : '',
    ].join(';');

    document.body.appendChild(el);
    activeEl = el;

    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
    const gap = 8;

    const placeAtBottomToast = () => {
      activeEl.style.left = '50%';
      activeEl.style.bottom = '18px';
      activeEl.style.top = '';
      activeEl.style.transform = 'translateX(-50%)';
    };

    const placeNearAnchor = () => {
      if (!activeEl || !anchorEl || typeof anchorEl.getBoundingClientRect !== 'function') {
        placeAtBottomToast();
        return;
      }

      const rect = anchorEl.getBoundingClientRect();
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;

      // 先放到屏幕外，拿到自身尺寸
      activeEl.style.left = '-9999px';
      activeEl.style.top = '-9999px';
      activeEl.style.bottom = '';
      activeEl.style.transform = 'translateX(0)';

      const tipW = activeEl.offsetWidth || 0;
      const tipH = activeEl.offsetHeight || 0;

      const preferRight = placement !== 'left';
      const canRight = rect.right + gap + tipW <= vw - 8;
      const canLeft = rect.left - gap - tipW >= 8;
      const useRight = preferRight ? (canRight || !canLeft) : !(canLeft || !canRight);

      const x = useRight ? (rect.right + gap) : (rect.left - gap - tipW);
      const yCenter = rect.top + rect.height / 2 - tipH / 2;
      const y = clamp(yCenter, 8, Math.max(8, vh - 8 - tipH));

      activeEl.style.left = `${Math.round(x)}px`;
      activeEl.style.top = `${Math.round(y)}px`;
      activeEl.style.bottom = '';
      // 轻微位移用于入场动画方向
      activeEl.style.transform = useRight ? 'translateX(-2px)' : 'translateX(2px)';
    };

    const updatePos = () => {
      if (!activeEl) return;
      if (anchorEl) placeNearAnchor();
      else placeAtBottomToast();
    };

    updatePos();

    if (anchorEl) {
      const onScroll = () => updatePos();
      const onResize = () => updatePos();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onResize);
      activeCleanup = () => {
        try { window.removeEventListener('scroll', onScroll, true); } catch (_) {}
        try { window.removeEventListener('resize', onResize); } catch (_) {}
      };
    }

    // 触发过渡
    try {
      window.requestAnimationFrame(() => {
        if (!activeEl) return;
        activeEl.style.opacity = '1';
        // toast: translateX(-50%) 已由 placeAtBottomToast 负责
        // anchor: translateX(±2px) 已由 placeNearAnchor 负责
        activeEl.style.transform = anchorEl ? 'translateX(0)' : 'translateX(-50%) translateY(-2px)';
      });
    } catch (_) {
      el.style.opacity = '1';
    }

    activeTimer = window.setTimeout(() => {
      try {
        if (!activeEl) return;
        const rm = activeEl;
        activeEl = null;
        try { if (typeof activeCleanup === 'function') activeCleanup(); } catch (_) {}
        activeCleanup = null;
        rm.style.opacity = '0';
        rm.style.transform = anchorEl ? 'translateY(2px)' : 'translateX(-50%) translateY(2px)';
        window.setTimeout(() => {
          try { if (rm.parentNode) rm.parentNode.removeChild(rm); } catch (_) {}
        }, 180);
      } catch (_) {}
    }, duration);
  } catch (_) {}
}

const HINT_PLUS_DURATION_MS = 6000;

/**
 * 增强提示条 HintPlus：用于权限等重要提示
 * - 显示 6 秒
 * - 屏幕正中央
 * - 大号字体
 * - 渐变蓝色底色
 */
export function showHintPlus(text, opts = {}) {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const msg = String(text ?? '').trim();
    if (!msg) return;

    const duration = Number(opts.duration) > 0 ? Number(opts.duration) : HINT_PLUS_DURATION_MS;

    if (hintPlusTimer) {
      window.clearTimeout(hintPlusTimer);
      hintPlusTimer = null;
    }
    if (hintPlusEl && hintPlusEl.parentNode) {
      hintPlusEl.parentNode.removeChild(hintPlusEl);
    }
    hintPlusEl = null;

    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = msg;
    el.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:50%',
      'transform:translate(-50%, -50%)',
      'z-index:2147483647',
      'max-width:min(100vw, 640px)',
      'padding:16px 24px',
      'border-radius:16px',
      'background:linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)',
      'color:#fff',
      'font-size:14px',
      'line-height:1.5',
      'letter-spacing:0.3px',
      'box-shadow:0 20px 50px rgba(37,99,235,0.35), 0 0 0 1px rgba(255,255,255,0.12)',
      'backdrop-filter:blur(12px)',
      '-webkit-backdrop-filter:blur(12px)',
      'pointer-events:none',
      'opacity:0',
      'transition:opacity 200ms ease',
      'text-align:center',
      'white-space:nowrap',
    ].join(';');

    document.body.appendChild(el);
    hintPlusEl = el;

    requestAnimationFrame(() => {
      if (hintPlusEl) hintPlusEl.style.opacity = '1';
    });

    hintPlusTimer = window.setTimeout(() => {
      if (!hintPlusEl) return;
      const rm = hintPlusEl;
      hintPlusEl = null;
      hintPlusTimer = null;
      rm.style.opacity = '0';
      window.setTimeout(() => {
        try { if (rm.parentNode) rm.parentNode.removeChild(rm); } catch (_) {}
      }, 220);
    }, duration);
  } catch (_) {}
}

